import { useState, useContext } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Calculator,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  Info,
  IndianRupee,
  Building2,
  Users,
  Calendar,
  Target,
  Shield,
  Lightbulb,
  BarChart3,
  PieChart,
  ArrowRight,
  Loader2,
  Sparkles
} from "lucide-react";

const INDUSTRIES = [
  "Manufacturing",
  "Retail",
  "Food & Beverage",
  "Technology",
  "Healthcare",
  "Education",
  "Real Estate",
  "Hospitality",
  "E-commerce",
  "Logistics",
  "Agriculture",
  "Financial Services",
  "Construction",
  "Automotive",
  "Textiles",
  "Pharmaceuticals",
  "Other"
];

export default function Valuation() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1);
  
  const [form, setForm] = useState({
    business_name: "",
    industry: "",
    location: "",
    years_in_operation: "",
    annual_revenue: "",
    annual_profit: "",
    total_assets: "",
    total_liabilities: "",
    employee_count: "",
    growth_rate: "",
    customer_base: "",
    competitive_position: "",
    unique_selling_points: "",
    reason_for_sale: ""
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatLakhs = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    }
    return `₹${(amount / 100000).toFixed(2)} L`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please login to use the valuation tool");
      navigate("/auth");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        years_in_operation: parseInt(form.years_in_operation) || 0,
        annual_revenue: parseFloat(form.annual_revenue) || 0,
        annual_profit: parseFloat(form.annual_profit) || 0,
        total_assets: parseFloat(form.total_assets) || 0,
        total_liabilities: parseFloat(form.total_liabilities) || 0,
        employee_count: parseInt(form.employee_count) || 0,
        growth_rate: parseFloat(form.growth_rate) || 0
      };

      const { data } = await api.post("/ai/valuation", payload);
      setResult(data);
      toast.success("Valuation generated successfully!");
    } catch (error) {
      console.error("Valuation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate valuation");
    } finally {
      setLoading(false);
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact) {
      case "positive": return <TrendingUp className="w-4 h-4 text-emerald-500" />;
      case "negative": return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "high": return "bg-red-100 text-red-700 border-red-200";
      case "medium": return "bg-amber-100 text-amber-700 border-amber-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const getConfidenceColor = (level) => {
    switch (level) {
      case "high": return "bg-emerald-100 text-emerald-700";
      case "medium": return "bg-amber-100 text-amber-700";
      default: return "bg-red-100 text-red-700";
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 pt-28 text-center">
          <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Business Valuation Estimator</h1>
          <p className="text-slate-600 mb-8">Please login to use the AI-powered valuation tool</p>
          <Button onClick={() => navigate("/auth")} className="bg-orange-500 hover:bg-orange-600">
            Sign In to Continue
          </Button>
        </div>
      </div>
    );
  }

  if (result) {
    const { valuation, input_summary } = result;
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8 pt-24">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Valuation Report</h1>
              <p className="text-slate-600">{input_summary.business_name} • {input_summary.industry}</p>
            </div>
            <Button variant="outline" onClick={() => { setResult(null); setStep(1); }}>
              New Valuation
            </Button>
          </div>

          {/* Valuation Summary Card */}
          <Card className="mb-8 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-orange-500" />
                <span className="text-sm font-medium text-orange-600 uppercase tracking-wider">Estimated Valuation</span>
                <Badge className={getConfidenceColor(valuation.confidence_level)}>
                  {valuation.confidence_level} confidence
                </Badge>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Low Estimate</p>
                  <p className="text-2xl font-bold text-slate-700">{formatLakhs(valuation.valuation_range.low)}</p>
                </div>
                <div className="text-center p-6 bg-orange-500 rounded-xl text-white transform scale-105 shadow-lg">
                  <p className="text-sm opacity-90 mb-1">Fair Value</p>
                  <p className="text-4xl font-bold">{formatLakhs(valuation.valuation_range.mid)}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl border border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">High Estimate</p>
                  <p className="text-2xl font-bold text-slate-700">{formatLakhs(valuation.valuation_range.high)}</p>
                </div>
              </div>

              <p className="text-slate-700 text-center">{valuation.summary}</p>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Methodology Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-600" />
                  Valuation Methodology
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">Primary Method: <span className="font-semibold">{valuation.primary_method}</span></p>
                <div className="space-y-3">
                  {valuation.methodology_breakdown?.map((method, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{method.method}</p>
                        <p className="text-xs text-slate-500">{method.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{formatLakhs(method.calculated_value)}</p>
                        <p className="text-xs text-slate-500">{method.weight}% weight</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Key Value Drivers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-600" />
                  Key Value Drivers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {valuation.key_value_drivers?.map((driver, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      {getImpactIcon(driver.impact)}
                      <div>
                        <p className="font-medium text-slate-900">{driver.factor}</p>
                        <p className="text-sm text-slate-600">{driver.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Risk Factors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Risk Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {valuation.risk_factors?.map((risk, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${getSeverityColor(risk.severity)}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium">{risk.risk}</p>
                        <Badge variant="outline" className={getSeverityColor(risk.severity)}>
                          {risk.severity}
                        </Badge>
                      </div>
                      <p className="text-sm opacity-80">Mitigation: {risk.mitigation}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Industry Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  Industry Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-purple-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-600 mb-1">Typical Industry Multiple</p>
                  <p className="text-2xl font-bold text-purple-900">{valuation.industry_comparison?.typical_multiple}</p>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-slate-600">Your Position:</span>
                  <Badge className={
                    valuation.industry_comparison?.position === "above_average" ? "bg-emerald-100 text-emerald-700" :
                    valuation.industry_comparison?.position === "below_average" ? "bg-red-100 text-red-700" :
                    "bg-slate-100 text-slate-700"
                  }>
                    {valuation.industry_comparison?.position?.replace("_", " ")}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600">{valuation.industry_comparison?.notes}</p>
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Recommendations to Improve Valuation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {valuation.recommendations?.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700">{rec}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Input Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-slate-600" />
                Input Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Annual Revenue</p>
                  <p className="font-semibold text-slate-900">{formatCurrency(input_summary.annual_revenue)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Annual Profit</p>
                  <p className="font-semibold text-slate-900">{formatCurrency(input_summary.annual_profit)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Net Worth</p>
                  <p className="font-semibold text-slate-900">{formatCurrency(input_summary.net_worth)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Profit Margin</p>
                  <p className="font-semibold text-slate-900">{input_summary.profit_margin}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-8">
            This valuation is an AI-generated estimate for informational purposes only. 
            For official valuations, please consult a certified business valuator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 text-sm font-bold px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            AI-POWERED
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Business Valuation Estimator</h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Get an instant AI-powered estimate of your business value using multiple valuation methodologies
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                step >= s ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {s}
              </div>
              <span className={`text-sm ${step >= s ? "text-slate-900" : "text-slate-400"}`}>
                {s === 1 ? "Basic Info" : s === 2 ? "Financials" : "Details"}
              </span>
              {s < 3 && <ArrowRight className="w-4 h-4 text-slate-300 ml-2" />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  Basic Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={form.business_name}
                      onChange={(e) => handleChange("business_name", e.target.value)}
                      placeholder="e.g., ABC Manufacturing Ltd"
                      required
                      data-testid="valuation-business-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry *</Label>
                    <Select value={form.industry} onValueChange={(val) => handleChange("industry", val)}>
                      <SelectTrigger data-testid="valuation-industry">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      value={form.location}
                      onChange={(e) => handleChange("location", e.target.value)}
                      placeholder="e.g., Mumbai, Maharashtra"
                      required
                      data-testid="valuation-location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="years_in_operation">Years in Operation *</Label>
                    <Input
                      id="years_in_operation"
                      type="number"
                      min="0"
                      value={form.years_in_operation}
                      onChange={(e) => handleChange("years_in_operation", e.target.value)}
                      placeholder="e.g., 5"
                      required
                      data-testid="valuation-years"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee_count">Employee Count *</Label>
                  <Input
                    id="employee_count"
                    type="number"
                    min="0"
                    value={form.employee_count}
                    onChange={(e) => handleChange("employee_count", e.target.value)}
                    placeholder="e.g., 25"
                    required
                    data-testid="valuation-employees"
                  />
                </div>
                <div className="flex justify-end">
                  <Button 
                    type="button" 
                    onClick={() => setStep(2)}
                    disabled={!form.business_name || !form.industry || !form.location || !form.years_in_operation}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Next: Financials
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Financials */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-orange-500" />
                  Financial Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="annual_revenue">Annual Revenue (₹) *</Label>
                    <Input
                      id="annual_revenue"
                      type="number"
                      min="0"
                      value={form.annual_revenue}
                      onChange={(e) => handleChange("annual_revenue", e.target.value)}
                      placeholder="e.g., 10000000"
                      required
                      data-testid="valuation-revenue"
                    />
                    <p className="text-xs text-slate-500">Enter in rupees (e.g., 1 Crore = 10000000)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="annual_profit">Annual Net Profit (₹) *</Label>
                    <Input
                      id="annual_profit"
                      type="number"
                      value={form.annual_profit}
                      onChange={(e) => handleChange("annual_profit", e.target.value)}
                      placeholder="e.g., 2000000"
                      required
                      data-testid="valuation-profit"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="total_assets">Total Assets (₹) *</Label>
                    <Input
                      id="total_assets"
                      type="number"
                      min="0"
                      value={form.total_assets}
                      onChange={(e) => handleChange("total_assets", e.target.value)}
                      placeholder="e.g., 15000000"
                      required
                      data-testid="valuation-assets"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_liabilities">Total Liabilities (₹) *</Label>
                    <Input
                      id="total_liabilities"
                      type="number"
                      min="0"
                      value={form.total_liabilities}
                      onChange={(e) => handleChange("total_liabilities", e.target.value)}
                      placeholder="e.g., 5000000"
                      required
                      data-testid="valuation-liabilities"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="growth_rate">Year-over-Year Growth Rate (%)</Label>
                  <Input
                    id="growth_rate"
                    type="number"
                    value={form.growth_rate}
                    onChange={(e) => handleChange("growth_rate", e.target.value)}
                    placeholder="e.g., 15"
                    data-testid="valuation-growth"
                  />
                </div>
                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => setStep(3)}
                    disabled={!form.annual_revenue || !form.annual_profit || !form.total_assets || !form.total_liabilities}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Next: Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Additional Details */}
          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-500" />
                  Additional Details (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_base">Customer Base Description</Label>
                  <Textarea
                    id="customer_base"
                    value={form.customer_base}
                    onChange={(e) => handleChange("customer_base", e.target.value)}
                    placeholder="e.g., 500+ B2B clients across India, 60% repeat customers, top clients include Fortune 500 companies"
                    rows={2}
                    data-testid="valuation-customers"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="competitive_position">Competitive Position</Label>
                  <Textarea
                    id="competitive_position"
                    value={form.competitive_position}
                    onChange={(e) => handleChange("competitive_position", e.target.value)}
                    placeholder="e.g., Market leader in Western India, 3 main competitors, strong brand recognition"
                    rows={2}
                    data-testid="valuation-competition"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unique_selling_points">Unique Selling Points</Label>
                  <Textarea
                    id="unique_selling_points"
                    value={form.unique_selling_points}
                    onChange={(e) => handleChange("unique_selling_points", e.target.value)}
                    placeholder="e.g., Proprietary technology, exclusive distribution rights, trained workforce, prime location"
                    rows={2}
                    data-testid="valuation-usp"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason_for_sale">Reason for Sale</Label>
                  <Textarea
                    id="reason_for_sale"
                    value={form.reason_for_sale}
                    onChange={(e) => handleChange("reason_for_sale", e.target.value)}
                    placeholder="e.g., Retirement, pursuing other opportunities, relocating"
                    rows={2}
                    data-testid="valuation-reason"
                  />
                </div>
                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="bg-orange-500 hover:bg-orange-600 min-w-[200px]"
                    data-testid="valuation-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Calculator className="w-4 h-4 mr-2" />
                        Generate Valuation
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>

        {/* Info Banner */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Your data is private</p>
              <p className="text-sm text-blue-700">
                Valuations are stored securely and only visible to you. This tool provides estimates for informational purposes only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
